import React, { useState, useEffect, useRef } from "react";
import { motion } from "motion/react";
import "./styles.css";

const Slider = ({
  min = 0,
  max = 100,
  step = 5,
  value: initialValue = 0,
  onChange = () => {},
  label,
  unit = "°",
  className = "",
}) => {
  const normalizeValue = (val) => {
    val = Math.max(min, Math.min(max, val));

    const steps = Math.round((val - min) / step);
    return Number((steps * step + min).toFixed(10));
  };

  const [actualValue, setActualValue] = useState(initialValue);

  const [localValue, setLocalValue] = useState(normalizeValue(initialValue));
  const previousValueRef = useRef(localValue);
  const sliderRef = useRef(null);

  useEffect(() => {
    setActualValue(initialValue);
    setLocalValue(normalizeValue(initialValue));
    previousValueRef.current = normalizeValue(initialValue);
  }, [initialValue, min, max, step]);

  // Calculate percentage for visual fill
  const percentage = max === min ? 0 : ((localValue - min) / (max - min)) * 100;

  const handleChange = (e) => {
    const newValue = parseFloat(e.target.value);
    const normalizedValue = normalizeValue(newValue);

    if (normalizedValue !== previousValueRef.current) {
      setLocalValue(normalizedValue);
      setActualValue(normalizedValue);
      previousValueRef.current = normalizedValue;
      onChange(normalizedValue);
    }
  };

  const formatDisplayValue = (value) => {
    const stepStr = step.toString();
    const valueStr = actualValue.toString();
    
    const stepDecimals = stepStr.includes(".") ? stepStr.split(".")[1].length : 0;
    const valueDecimals = valueStr.includes(".") ? valueStr.split(".")[1].length : 0;
    const decimalPlaces = Math.max(stepDecimals, valueDecimals);
    
    const diff = Math.abs(actualValue - localValue);
    const tolerance = step / 100;
    
    if (diff > tolerance) {
      return actualValue.toFixed(decimalPlaces);
    }
    
    return localValue.toFixed(decimalPlaces);
  };

  return (
    <div className={`slider-container ${className}`}>
      <div className="slider-wrapper">
        <div className="slider" ref={sliderRef}>
          {/* Custom slider track */}
          <div className="slider-track">
            {/* Filled portion with gradient */}
            <motion.div
              className="slider-fill"
              style={{ width: `${percentage}%` }}
              animate={{
                width: `${percentage}%`,
              }}
              transition={{ type: "spring", stiffness: 300, damping: 30 }}
            />

            {/* Label and value inside the slider */}
            <div className="slider-content">
              <span className="slider-label">{label}</span>
              <motion.div
                key={localValue}
                initial={{ opacity: 0.9, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                className="slider-value-container "
              >
                <span className="slider-value">
                  {formatDisplayValue(localValue)}
                  {unit}
                </span>
              </motion.div>
            </div>
          </div>

          <input
            type="range"
            min={min}
            max={max}
            step={step}
            value={localValue}
            onChange={handleChange}
            className="slider-input"
            aria-label={`${label} slider`}
          />
        </div>
      </div>
    </div>
  );
};
export default Slider;
